<template>
  <div
    @mouseenter="showDownloadLink = true"
    @mouseleave="showDownloadLink = false"
    class="track-waveform"
    :style="style"
  >
    <transition name="fade">
      <a
        v-if="showDownloadLink"
        :href="trackDownloadLink"
        class="track-download"
        :style="downloadLinkStyle"
      >
        <cloud-arrow-down-icon />
      </a>
    </transition>
  </div>
</template>

<script lang="ts">
import { WAVEFORM_SEC_PER_PIXEL } from "@/misc";
import { MixTrack, Tag, Track } from "@/models";
import { Options, Vue } from "vue-class-component";
import { mapGetters, mapState } from "vuex";
import { CloudArrowDownIcon } from "@heroicons/vue/24/outline";

@Options({
  components: {
    CloudArrowDownIcon,
  },
  props: {
    mixTrack: {
      type: Object,
      required: true,
    },
    scrollPosition: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    ...mapGetters("projects", ["trackMap"]),
    ...mapState("projects", ["tags"]),
    ...mapState("mixes", ["trackHeight"]),
  },
  data() {
    return {
      showDownloadLink: false,
    };
  },
})
export default class TrackWaveform extends Vue {
  mixTrack!: MixTrack;
  trackMap!: Record<string, Track>;
  tags!: Record<string, Tag>;
  trackHeight!: number;
  showDownloadLink!: boolean;
  scrollPosition!: number;

  get track() {
    return this.trackMap[this.mixTrack.track_id];
  }

  get trackDownloadLink() {
    return `/api/v1/tracks/${this.track.filehash}?download=true`;
  }

  get waveformPath() {
    return `/api/v1/tracks/${this.track.filehash}/waveform/${this.track.waveform_ts}`;
  }

  get waveformWidth() {
    const trackLen = this.track.length_ms / 1000; // in secs
    return trackLen / WAVEFORM_SEC_PER_PIXEL;
  }

  get trackTag() {
    return this.tags[this.track.tag_id];
  }

  get style() {
    const backgroundColor = this.trackTag
      ? `#${this.trackTag.color}30`
      : "#ffffff30";
    return {
      backgroundImage: `url(${this.waveformPath})`,
      backgroundColor,
      width: `${this.waveformWidth}px`,
      height: `${this.trackHeight}px`,
      backgroundSize: "100% 50px",
    };
  }

  get downloadLinkStyle() {
    return {
      left: `${4 + this.scrollPosition}px`,
    };
  }
}
</script>

<style lang="scss">
.track-waveform {
  overflow: auto;
  box-sizing: border-box;
  border-bottom: 1px solid #ccc;
  background: linear-gradient(180deg, #f0f0f0 0%, #e0e0e0 100%);
  background-blend-mode: multiply;
  position: relative;

  .track-download {
    display: block;
    position: absolute;
    bottom: 2px;
    width: 20px;
    height: 20px;
    color: rgba(0, 0, 0, 0.5);
  }
}
</style>
